// src/components/income/IncomeEmploymentFields.tsx
import React, { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import AddressInput from "../AddressInput";

interface Props {
  company: string;
  address: string;
  phone: string;
  jobTitle: string;
  monthlyNetIncome: number;
  startDate: string;
  current: boolean;
  endDate: string;
  onCompanyChange: (val: string) => void;
  onAddressChange: (val: string) => void;
  onPhoneChange: (val: string) => void;
  onJobTitleChange: (val: string) => void;
  onMonthlyIncomeChange: (val: number) => void;
  onStartDateChange: (val: string) => void;
  onCurrentToggle: () => void;
  onEndDateChange: (val: string) => void;
}

export const IncomeEmploymentFields: React.FC<Props> = ({
  company,
  address,
  phone,
  jobTitle,
  monthlyNetIncome,
  startDate,
  current,
  endDate,
  onCompanyChange,
  onAddressChange,
  onPhoneChange,
  onJobTitleChange,
  onMonthlyIncomeChange,
  onStartDateChange,
  onCurrentToggle,
  onEndDateChange,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="mb-4">
        <label htmlFor="company" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.employment.companyLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="company"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={company}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onCompanyChange(e.target.value)
          }
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="address" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.employment.addressLabel")}
        </label>
        <AddressInput
          id="address"
          value={address}
          onChange={onAddressChange}
          required={false}
        />
      </div>

      <div className="mb-4">
        <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.employment.phoneLabel")}
        </label>
        <PhoneInput
          id="phone"
          international={false}
          defaultCountry="US"
          value={phone}
          onChange={(val) => onPhoneChange(val || "")}
          placeholder="(123) 456-7890"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="jobTitle" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.employment.jobTitleLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          id="jobTitle"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={jobTitle}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onJobTitleChange(e.target.value)
          }
          required
        />
      </div>

      <div className="mb-4">
        <label htmlFor="monthlyNetIncome" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.employment.monthlyIncomeLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <div className="flex items-center rounded-lg border border-gray-300">
          <span className="border-r border-gray-300 bg-gray-50 px-3 py-2.5 text-sm text-gray-500">$</span>
          <input
            type="number"
            id="monthlyNetIncome"
            className="w-full rounded-r-lg border-0 px-4 py-2.5 text-sm transition-all focus:ring-2 focus:ring-primary/20 focus:outline-none"
            value={monthlyNetIncome}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onMonthlyIncomeChange(parseFloat(e.target.value) || 0)
            }
            step="0.01"
            required
          />
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="startDate" className="mb-1.5 block text-sm font-medium text-gray-700">
          {t("income.employment.startDateLabel")}{" "}
          <span className="text-danger">*</span>
        </label>
        <input
          type="date"
          id="startDate"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
          value={startDate}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            onStartDateChange(e.target.value)
          }
          required
        />
      </div>

      <div className="mb-4 flex items-center gap-2">
        <input
          type="checkbox"
          id="current"
          className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary/20"
          checked={current}
          onChange={onCurrentToggle}
        />
        <label htmlFor="current" className="text-sm text-gray-700">
          {t("income.employment.currentLabel")}
        </label>
      </div>

      {!current && (
        <div className="mb-4">
          <label htmlFor="endDate" className="mb-1.5 block text-sm font-medium text-gray-700">
            {t("income.employment.endDateLabel")}
          </label>
          <input
            type="date"
            id="endDate"
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
            value={endDate}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onEndDateChange(e.target.value)
            }
          />
        </div>
      )}
    </>
  );
};
